import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge, inr, fmtDateTime } from "@/components/common";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const LIVE = (r) => !r.meta || r.status === "ACTIVE" || !r.status;

export default function Campaigns() {
  const { user } = useAuth();
  const canSync = ["super_admin", "admin", "marketing"].includes(user.role);
  const money = (r, v) => (r.meta && !r.budget ? "—" : inr(v));
  const [data, setData] = useState(null);
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/campaigns").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const sync = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/meta/ad-campaigns/sync");
      r.error ? toast.error(r.error) : toast.success(`${r.count} Facebook/Instagram campaigns synced`);
      load();
    } finally { setBusy(false); }
  };
  const spend = data?.show_spend;
  const columns = [
    { key: "name", label: "Campaign", render: (r) => <div><div className="font-semibold text-slate-800" data-testid={`campaign-name-${r.id}`}>{r.name}</div><div className="text-[11px] text-slate-400">{r.platform}{r.objective ? ` · ${r.objective.replace("OUTCOME_", "").toLowerCase()}` : ""}</div></div> },
    { key: "course", label: "Course / Started", render: (r) => r.meta ? <span className="text-xs">{r.start_time ? fmtDateTime(r.start_time) : "—"}</span> : r.course },
    ...(spend ? [{ key: "budget", label: "Budget / Spend", render: (r) => money(r, r.budget) }] : []),
    { key: "leads", label: "Leads" },
    { key: "qualified", label: "Qualified" },
    { key: "admissions", label: "Admissions", render: (r) => <span className="font-bold text-[#4F46E5]">{r.admissions}</span> },
    ...(spend ? [
      { key: "cost_per_lead", label: "Cost/Lead", render: (r) => money(r, r.cost_per_lead) },
      { key: "cost_per_admission", label: "Cost/Adm", render: (r) => money(r, r.cost_per_admission) },
    ] : []),
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.meta && !r.status ? "Receiving leads" : r.status} /> },
  ];
  const rows = data ? data.rows.filter((r) => all || LIVE(r)) : null;
  const s = data?.ads_sync || {};
  return (
    <div>
      <PageHeader title="Campaigns & Marketing ROI" subtitle="Which campaigns generate actual admissions — not just leads" testid="campaigns-header" />
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="campaigns-show-all" /> Show paused / ended campaigns
        </label>
        {spend && canSync && <>
          <Button size="sm" variant="outline" onClick={sync} disabled={busy} data-testid="campaigns-sync-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Sync from Ads Manager</Button>
          <span className="text-xs text-slate-400" data-testid="campaigns-sync-status">{s.last_run ? `Last synced ${fmtDateTime(s.last_run)}` : "Not synced yet"}{s.last_error ? <span className="text-amber-600"> · {s.last_error}</span> : ""}</span>
        </>}
      </div>
      <DataTable columns={columns} rows={rows} loading={rows === null} testid="campaigns-table" />
    </div>
  );
}
